import React, { FC, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { shadeSafe } from '../../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../../Legacy';
import { Matrix } from '../../../platform/geom/Matrix';
import { Rect } from '../../../platform/geom/Rect';
import { Colors } from '../../../ui';
import { RenderBounds, RenderBox, RenderInner, usePose, usePressable } from '../../canvas/visualization/RenderHelpers';
import { RenderText } from '../../canvas/visualization/RenderText';
import { DropZone, DropZoneMode, DropZoneUser } from './DropZone';
import { DropZoneLabelFieldTextSettings } from './DropZoneLabelField';

const AutoSizeDropZoneLabelFieldTextSettings = { ...DropZoneLabelFieldTextSettings, fontSize: 0 };

const RenderButton = React.memo<{ area: Rect, interaction: string, children: ReactNode }>(({ area, interaction, children }) => {

    const transform =
        (interaction === 'idle' ? Matrix.translate(0, -2).toCSS() : null) ??
        (interaction === 'hover' ? Matrix.translate(0, -4).toCSS() : null) ??
        (interaction === 'active' ? Matrix.translate(0, 0).toCSS() : null);

    const backStyle = {
        ...area.toCSS(),
        background: Colors.gray300,
        borderRadius: '8px',
    };

    const foreStyle = {
        ...area.toCSS(),
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '0 5px',
        fontFamily: 'Rubik',
        fontSize: Math.round(area.height / 2.5),
        fontWeight: 500,
        fontColor: Colors.textLight,
        textAlign: 'center',
        lineHeight: '1.2em',
        background: Colors.gray200,
        borderRadius: '8px',
        transform,
        transition: 'transform 75ms ease-out',
    };

    return <>
        <RenderBox {...backStyle} />
        <RenderBox {...foreStyle}>
            {children}
        </RenderBox>
    </>;
});

const RenderBorder: LFC<{ object: DropZone }> = ({ object }) => {
    const { base } = object.layout;
    if (!object.background) return null;
    return <>
        <RenderBox
            {...base.toCSS()}
            background={object.background}
            borderRadius="16px"
            border={`solid 1px ${shadeSafe(.1, object.background)}`}
            borderBottom={`solid 3px ${shadeSafe(.1, object.background)}`}
        />
    </>;
};

const setLightText = (x: any) => ({ ...x, color: Colors.textLightest });

const ForceSpanStyle = styled.div<{ color?: string }>`
    span {
        ${p => p.color && css`
            color: ${Colors.contrastTextColorFor(p.color)};
            background: ${p.color} !important;
            padding: 0 4px !important;
        `}
    }
`;

const RenderLabel: FC<{ object: DropZone }> = ({ object }) => {
    const ptpt = DropZoneUser.get(object);
    return <ForceSpanStyle color={ptpt?.color}>
        <RenderText
            bounds={object.layout.label}
            settings={AutoSizeDropZoneLabelFieldTextSettings}
            value={ptpt?.name ?? object.actualLabel}
            customize={object.mode === DropZoneMode.participant ? setLightText : null}
        />
    </ForceSpanStyle>;
};

const RenderPeek = React.memo<{ area: Rect, interaction: string }>(({ area, interaction }) => {
    if (!area.area()) return null;
    // return <RenderBox {...area.toCSS()} borderRadius="4px" background="rgba(0,0,0,.15)">
    // </RenderBox>;
    return <RenderButton area={area} interaction={interaction}>
        {/* <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" style={{ display: 'inline-block', width: '1.5em', height: '1.5em' }}>
            <path fill="currentColor" d="M 20 10 C 16.686 10 14 12.686 14 16 L 14 48 C 14 51.314 16.686 54 20 54 L 36.699219 54 C 34.895219 53.107 33.387563 51.715 32.351562 50 L 20 50 C 18.895 50 18 49.105 18 48 L 18 16 C 18 14.895 18.895 14 20 14 L 30 14 L 30 24 C 30 27.314 32.686 30 36 30 L 46 30 L 46 35.199219 C 47.61 36.002219 48.99 37.199828 50 38.673828 L 50 28.65625 C 50 27.06425 49.367188 25.539062 48.242188 24.414062 L 35.585938 11.757812 C 34.459938 10.632812 32.93475 10 31.34375 10 L 20 10 z M 34 15.828125 L 44.171875 26 L 36 26 C 34.895 26 34 25.105 34 24 L 34 15.828125 z M 41.335938 37.09375 C 37.199937 37.09375 33.835938 40.45775 33.835938 44.59375 C 33.835938 48.72975 37.199937 52.09375 41.335938 52.09375 C 42.547937 52.09375 43.689125 51.798156 44.703125 51.285156 L 49.525391 56.107422 C 49.916391 56.498422 50.427453 56.693359 50.939453 56.693359 C 51.451453 56.693359 51.962516 56.498422 52.353516 56.107422 C 53.134516 55.326422 53.134516 54.060297 52.353516 53.279297 L 47.667969 48.59375 C 48.402969 47.43475 48.835938 46.06475 48.835938 44.59375 C 48.835937 40.45775 45.471938 37.09375 41.335938 37.09375 z M 41.335938 41.09375 C 43.265938 41.09375 44.835938 42.66375 44.835938 44.59375 C 44.835938 46.52375 43.265937 48.09375 41.335938 48.09375 C 39.405937 48.09375 37.835938 46.52375 37.835938 44.59375 C 37.835938 42.66375 39.405938 41.09375 41.335938 41.09375 z"></path>
        </svg> */}
        Peek
    </RenderButton>;
});

export const DropZoneVisual: LFC<{ object: DropZone }> = ({ object, children }) => {
    const { beingEdited } = usePose(object);
    const peek = usePressable(object, 'peek');

    return <>
        <RenderBounds object={object}>
            <RenderInner object={object}>
                <RenderBorder object={object} />
                <RenderPeek area={object.layout.peek} interaction={peek} />
                {!beingEdited && <RenderLabel object={object} />}
            </RenderInner>
        </RenderBounds>
        {children}
    </>;
};
